import Tracker from "../../../../../prop-trackers/tracker.js";
import ValueTracker from "../../../../../prop-trackers/value-tracker.js";
import PropTracker from "../../../../../prop-trackers/prop-tracker.js";
import DictTracker from "../../../../../prop-trackers/dict-tracker.js";
import ConsumingDictMux from "../../../../../prop-tracker-utils/consuming-dict-mux.js";
import Zipper from "../../../../../prop-tracker-utils/zipper.js";
import { ForEach, ValueTracker as ForEachValueTracker } from "../../../../../prop-tracker-utils/for-each.js";

class TrackMap {
    constructor() {
        this._data = new Map();
    }

    set(object, property, value) {
        let objectTracks = this._data.get(object);

        if (!objectTracks) {
            objectTracks = new Map();
            this._data.set(object, objectTracks);
        }

        objectTracks.set(property, value);
    }

    delete(object, property) {
        const objectTracks = this._data.get(object);

        if (!objectTracks) {
            return;
        }

        objectTracks.delete(property);

        if (objectTracks.size === 0) {
            this._data.delete(object);
        }
    }

    get(object, property) {
        const objectTracks = this._data.get(object);

        if (!objectTracks) {
            return;
        }

        return objectTracks.get(property);
    }
}

function isEmpty(obj) {
    for (const key in obj) {
        return false;
    }

    return true;
}

class ObjectValueGenerator extends Tracker {
    constructor(objectAnimKeyTracker, objectTrackTracker, animTracker, timeTracker, wrapCtx, evtCtx) {
        super();

        this._wrapCtx = wrapCtx;
        this._evtCtx = evtCtx;
        this._objectAnimValues = {};

        const needsUpdate = () => this.protected_needsUpdate();

        const objectAnimTracker = new ConsumingDictMux(animTracker, objectAnimKeyTracker);

        this._zipper = new Zipper();
        this._zipper.listenNeedsUpdate(needsUpdate);

        this._zipper.setSource("animation", objectAnimTracker);
        this._zipper.setSource("tracks", objectTrackTracker);

        this._objAnimTrackTracker = new DictTracker();
        this._objTrackDataMapper = new ForEach(this._objAnimTrackTracker, (key) => new ObjectTrackDataMapper(evtCtx, key));
        this._objTrackDataMapper.listenNeedsUpdate(needsUpdate);

        this._trackMap = new TrackMap();
        this._tracks = new Set();
        this._newTracks = new Set();

        this._timeTracker = timeTracker;
        this._timeTracker.listenNeedsUpdate(needsUpdate);

        this._outputTracker = new DictTracker();
    }

    protected_startUpdate() {
        this._zipper.updateIfNeeded(({ trackers }) => {
            for (const tracker of trackers) {
                tracker.update(({ userdata: objKey, props }) => {
                    const animation = props.get("animation").newValue;
                    const tracks = props.get("tracks").newValue;

                    if (animation && tracks) {
                        this._objAnimTrackTracker.setValue(objKey, { animation, tracks });
                    } else {
                        this._objAnimTrackTracker.deleteValue(objKey);
                    }
                });
            }
        });

        this._objTrackDataMapper.updateIfNeeded(({ trackers }) => {
            for (const tracker of trackers) {
                tracker.update(({ userdata: object, props }) => {
                    for (const { key: property, newValue: trackData } of props.iterateModified()) {
                        const oldData = this._trackMap.get(object, property);

                        let result;

                        if (oldData) {
                            result = oldData.result;

                            if (!trackData) {
                                const objAnimValues = this._objectAnimValues[object];
                                delete objAnimValues[property];

                                if (isEmpty(objAnimValues)) {
                                    delete this._objectAnimValues[object];
                                    this._outputTracker.deleteValue(object);
                                }
                            }

                            this._tracks.delete(oldData);
                            this._newTracks.delete(oldData);
                        }

                        if (trackData) {
                            const { track, duration } = trackData;

                            if (!result) {
                                result = this._wrapCtx.getWrapper([]);

                                if (!(object in this._objectAnimValues)) {
                                    const objAnimValues = this._wrapCtx.getWrapper({});
                                    this._objectAnimValues[object] = objAnimValues;
                                    this._outputTracker.setValue(object, objAnimValues);
                                }

                                this._objectAnimValues[object][property] = result;
                            } else {
                                result.length = 0;
                            }

                            const interpolant = track.createInterpolant();
                            const newData = { duration, interpolant, result };

                            this._trackMap.set(object, property, newData);
                            this._tracks.add(newData);
                            this._newTracks.add(newData);
                        } else if (oldData) {
                            this._trackMap.delete(object, property);
                        }
                    }
                });
            }
        });

        const updateTime = this._timeTracker.needsUpdate;
        if (updateTime || this._newTracks.size !== 0) {
            this._timeTracker.update(({ prop: { newValue: time } }) => {
                const tracksToUpdate = updateTime ? this._tracks : this._newTracks;

                for (const { duration, interpolant, result } of tracksToUpdate) {
                    const progress = time/duration;
                    const animTime = (progress-Math.floor(progress))*duration;

                    const newValue = interpolant.evaluate(animTime);
                    for (const [i, value] of newValue.entries()) {
                        result[i] = value;
                    }
                }
            });

            this._newTracks.clear();
        }

        return this._outputTracker.startUpdate();
    }

    protected_finishUpdate() {
        return this._outputTracker.finishUpdate();
    }
}

const EmptyAnim = { tracks: [] };

class ObjectTrackDataMapper extends ForEachValueTracker {
    constructor(evtCtx, key) {
        super();

        this._evtCtx = evtCtx;
        this._key = key;

        this._animTracker = new ValueTracker();
        this._trackIndexTracker = new PropTracker();
        this._outputTracker = new DictTracker(key);

        const needsUpdate = () => this.protected_needsUpdate();
        this._animTracker.listenNeedsUpdate(needsUpdate);
        this._trackIndexTracker.listenNeedsUpdate(needsUpdate);
    }

    setValue(data) {
        let animation, tracks;

        if (data) {
            ({ animation, tracks } = data);
        }

        if (animation && tracks) {
            this._animTracker.setValue(animation);
            this._trackIndexTracker.track(tracks, this._evtCtx.getEvents(tracks));
        } else {
            this.deleteValue();
        }
    }

    deleteValue() {
        this._animTracker.deleteValue();
        this._trackIndexTracker.track();
    }

    protected_startUpdate() {
        const updateAnim = this._animTracker.needsUpdate;

        if (updateAnim || this._trackIndexTracker.needsUpdate) {
            this._animTracker.update(({ prop: { newValue: animation } }) => {
                this._trackIndexTracker.update(({ props }) => {
                    const { tracks, duration } = animation || EmptyAnim;
                    const object = this._key;
                    const propIterator = updateAnim ? props.iterateAll() : props.iterateModified();

                    for (const { key: property, newValue: index } of propIterator) {
                        const track = tracks[index];

                        if (track) {
                            const data = { track, duration };
                            this._outputTracker.setValue(property, data);
                        } else {
                            this._outputTracker.deleteValue(property);
                        }
                    }
                });
            });
        }

        return this._outputTracker.startUpdate();
    }

    protected_finishUpdate() {
        this._outputTracker.finishUpdate();
    }
}

export default ObjectValueGenerator;
